import { format } from 'date-fns'
import jsPDF from 'jspdf'

import type { QrCodeMetadata } from '../types/qr-codes'

// ----------------------------------------------------------------------

/**
 * Generates a PDF document containing a QR code with branch and metadata information
 *
 * @param metadata - QR code metadata including branch info, dates, and location details
 * @param qrCodeDataUrl - Base64 data URL of the QR code image from canvas
 * @returns Promise that resolves when PDF is generated and download is triggered
 * @throws Error if PDF generation fails
 */
export async function generateQrCodePdf(
  metadata: QrCodeMetadata,
  qrCodeDataUrl: string
): Promise<void> {
  try {
    // Validate data URL
    if (!qrCodeDataUrl || qrCodeDataUrl === '') {
      throw new Error('QR code image data is missing')
    }

    if (!qrCodeDataUrl.startsWith('data:image/png;base64,')) {
      console.error('Invalid data URL format:', qrCodeDataUrl.substring(0, 50))
      throw new Error('Invalid QR code image format. Expected PNG data URL.')
    }

    // Create new PDF document (A4, portrait)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    // A4 dimensions: 210mm x 297mm
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20

    // Current Y position for content
    let yPosition = margin

    // ===== HEADER SECTION =====
    // Branch name
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(metadata.branchName, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 10

    // Branch address
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    const addressLines = doc.splitTextToSize(metadata.branchAddress, pageWidth - 2 * margin)
    doc.text(addressLines, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += addressLines.length * 7 + 10

    // ===== QR CODE SECTION =====
    // Calculate QR code dimensions and position - make it as big as possible
    // Leave space for header (already used) and footer (dates ~20mm)
    const availableHeight = pageHeight - yPosition - 40 // Reserve 40mm for footer
    const maxSize = Math.min(pageWidth - 2 * margin, availableHeight)
    const qrSize = maxSize // Use maximum available space
    const qrX = (pageWidth - qrSize) / 2
    const qrY = yPosition

    // Add QR code image with explicit format
    try {
      doc.addImage(qrCodeDataUrl, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'FAST')
    } catch (imageError) {
      console.error('Error adding image to PDF:', imageError)
      throw new Error('Failed to add QR code image to PDF. The image may be corrupted.')
    }
    yPosition = qrY + qrSize + 15

    // ===== FOOTER SECTION (Metadata) =====
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')

    // Generated date (Turkish - using ASCII-safe characters)
    const generatedDate = format(new Date(metadata.generatedAt), 'dd/MM/yyyy HH:mm')
    doc.text(`Olusturma Tarihi: ${generatedDate}`, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 8

    // Expiration date (Turkish - using ASCII-safe characters)
    const expirationText = metadata.expiresAt
      ? format(new Date(metadata.expiresAt), 'dd/MM/yyyy HH:mm')
      : 'Suresiz'
    doc.text(`Gecerlilik Sonu: ${expirationText}`, pageWidth / 2, yPosition, { align: 'center' })
    yPosition += 8

    // Generate filename with timestamp
    const timestamp = format(new Date(), 'yyyyMMdd-HHmmss')
    const sanitizedBranchName = metadata.branchName.replace(/[^a-zA-Z0-9]/g, '-')
    const filename = `QR-${sanitizedBranchName}-${timestamp}.pdf`

    // Trigger download
    doc.save(filename)
  } catch (error) {
    console.error('Failed to generate PDF:', error)
    throw new Error(
      `PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}
